import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import da from 'date-fns/locale/da';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useQuery } from '@tanstack/react-query';
import PlacesAutocomplete, { PlaceResult } from './PlacesAutocomplete';
import { api } from '../../services/api';
import type { RouteWithDetails, Trailer, Driver, SplitRouteResult } from '../../types';

interface SplitRouteDialogProps {
  open: boolean;
  onClose: () => void;
  route: RouteWithDetails | null;
  onSplit: (routeId: string, params: {
    trailerId: string;
    cargoIds: string[];
    reloadAddress: PlaceResult;
    reloadDate: string;
    deliveryRouteOption: 'undispatched' | 'newRoute';
    deliveryRouteDriverId?: string;
    deliveryRouteTrailerId?: string;
  }) => Promise<SplitRouteResult>;
  isLoading?: boolean;
}

export default function SplitRouteDialog({
  open,
  onClose,
  route,
  onSplit,
  isLoading,
}: SplitRouteDialogProps) {
  const [reloadAddress, setReloadAddress] = useState<PlaceResult | null>(null);
  const [reloadDate, setReloadDate] = useState<Date | null>(new Date());
  const [deliveryOption, setDeliveryOption] = useState<'undispatched' | 'newRoute'>('undispatched');
  const [deliveryDriverId, setDeliveryDriverId] = useState<string>('');
  const [deliveryTrailerId, setDeliveryTrailerId] = useState<string>('');
  const [selectedCargoIds, setSelectedCargoIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Fetch trailers and drivers for dropdowns
  const { data: trailers } = useQuery({
    queryKey: ['trailers'],
    queryFn: () => api.getTrailers(),
    enabled: open,
  });

  const { data: drivers } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => api.getDrivers(),
    enabled: open && deliveryOption === 'newRoute',
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open && route) {
      setReloadAddress(null);
      setReloadDate(new Date());
      setDeliveryOption('undispatched');
      setDeliveryDriverId('');
      setDeliveryTrailerId('');
      setError(null);
      // Default select all non-delivered cargos
      const nonDeliveredIds = new Set(
        route.cargos
          .filter(c => !c.isDelivered)
          .map(c => c.id)
      );
      setSelectedCargoIds(nonDeliveredIds);
    }
  }, [open, route]);

  const handleClose = () => {
    setReloadAddress(null);
    setReloadDate(new Date());
    setDeliveryOption('undispatched');
    setDeliveryDriverId('');
    setDeliveryTrailerId('');
    setSelectedCargoIds(new Set());
    setError(null);
    onClose();
  };

  const handleToggleCargo = (cargoId: string) => {
    setSelectedCargoIds(prev => {
      const next = new Set(prev);
      if (next.has(cargoId)) {
        next.delete(cargoId);
      } else {
        next.add(cargoId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (route) {
      setSelectedCargoIds(new Set(route.cargos.map(c => c.id)));
    }
  };

  const handleDeselectAll = () => {
    setSelectedCargoIds(new Set());
  };

  const handleSplit = async () => {
    if (!route || !reloadAddress || !reloadDate) return;

    if (!reloadAddress.address || !reloadAddress.city) {
      setError('Indtast en gyldig adresse med by');
      return;
    }

    if (selectedCargoIds.size === 0) {
      setError('Vælg mindst én ordre at dele');
      return;
    }

    setError(null);
    try {
      const dateStr = reloadDate.toISOString().split('T')[0];
      await onSplit(route.id, {
        trailerId: route.trailerId,
        cargoIds: Array.from(selectedCargoIds),
        reloadAddress,
        reloadDate: dateStr,
        deliveryRouteOption: deliveryOption,
        deliveryRouteDriverId: deliveryOption === 'newRoute' && deliveryDriverId ? deliveryDriverId : undefined,
        deliveryRouteTrailerId: deliveryOption === 'newRoute' && deliveryTrailerId ? deliveryTrailerId : undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Der opstod en fejl');
    }
  };

  const routeName = route?.routeName || `Rute ${route?.id?.slice(0, 8) || ''}`;
  const fromCity = route?.firstStop?.city || 'Ukendt';
  const toCity = route?.lastStop?.city || 'Ukendt';
  const trailerDisplay = route?.trailerAlias || route?.trailerNumber || 'Ukendt';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={da}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CallSplitIcon />
          Split rute
        </DialogTitle>
        <DialogContent>
          {route && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Route info - compact single line */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>{routeName}</Typography>
                <Typography variant="body2" color="text.secondary">{fromCity} → {toCity}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalShippingIcon sx={{ fontSize: 16 }} color="action" />
                  <Typography variant="body2" color="text.secondary">{trailerDisplay}</Typography>
                </Box>
                <Typography variant="body2" color="primary" fontWeight={500}>
                  {route.cargoCount} ordrer
                </Typography>
              </Box>

              {/* Cargo Selection */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Vælg ordrer at dele ({selectedCargoIds.size} af {route.cargoCount} valgt)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" onClick={handleSelectAll}>
                      Vælg alle
                    </Button>
                    <Button size="small" onClick={handleDeselectAll}>
                      Fravælg alle
                    </Button>
                  </Box>
                </Box>
                <TableContainer sx={{ maxHeight: 200, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>Ref</TableCell>
                        <TableCell>Kunde</TableCell>
                        <TableCell>Fra</TableCell>
                        <TableCell>Til</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {route.cargos.map((cargo) => (
                        <TableRow
                          key={cargo.id}
                          hover
                          onClick={() => handleToggleCargo(cargo.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedCargoIds.has(cargo.id)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {cargo.ref}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                              {cargo.customerName || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {cargo.firstStop?.city || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {cargo.lastStop?.city || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {cargo.isDelivered ? (
                              <Chip label="Leveret" size="small" color="success" />
                            ) : (
                              <Chip label="Aktiv" size="small" color="primary" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider />

              {/* Explanation */}
              <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  <strong>1/3:</strong> Ny rute ({trailerDisplay}) · <strong>2/3:</strong> Denne rute · <strong>3/3:</strong> Vælg nedenfor
                </Typography>
              </Alert>

              {/* Reload address input */}
              <PlacesAutocomplete
                label="Omlæsningsadresse"
                value={reloadAddress}
                onChange={setReloadAddress}
                error={!!error && !reloadAddress}
              />

              {/* Reload date picker */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Omlæsningsdato
                </Typography>
                <DatePicker
                  value={reloadDate}
                  onChange={setReloadDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>

              {/* Show selected address details */}
              {reloadAddress && reloadAddress.city && (
                <Box sx={{ bgcolor: 'success.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                  <Typography variant="subtitle2" color="success.dark" gutterBottom>
                    Valgt omlæsningssted
                  </Typography>
                  <Typography variant="body2">
                    <strong>Adresse:</strong> {reloadAddress.address}
                  </Typography>
                  <Typography variant="body2">
                    <strong>By:</strong> {reloadAddress.postcode} {reloadAddress.city}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Land:</strong> {reloadAddress.country}
                  </Typography>
                </Box>
              )}

              <Divider />

              {/* Delivery option */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Destination for 3/3-ordrer (omlæsning → levering)
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={deliveryOption}
                    onChange={(e) => setDeliveryOption(e.target.value as 'undispatched' | 'newRoute')}
                  >
                    <FormControlLabel
                      value="undispatched"
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2">
                          Efterlad udisponeret
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="newRoute"
                      control={<Radio size="small" />}
                      label={
                        <Typography variant="body2">
                          Opret ny rute
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {/* Show trailer/driver selection when newRoute is selected */}
                {deliveryOption === 'newRoute' && (
                  <Box sx={{ ml: 3, mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Køretøj (valgfrit)</InputLabel>
                      <Select
                        value={deliveryTrailerId}
                        label="Køretøj (valgfrit)"
                        onChange={(e) => setDeliveryTrailerId(e.target.value)}
                      >
                        <MenuItem value="">Ingen</MenuItem>
                        {trailers?.map((t: Trailer) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.alias || t.number}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Chauffør (valgfrit)</InputLabel>
                      <Select
                        value={deliveryDriverId}
                        label="Chauffør (valgfrit)"
                        onChange={(e) => setDeliveryDriverId(e.target.value)}
                      >
                        <MenuItem value="">Ingen</MenuItem>
                        {drivers?.map((d: Driver) => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.firstName} {d.lastName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>

              {error && (
                <Alert severity="error">{error}</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Annuller
          </Button>
          <Button
            variant="contained"
            onClick={handleSplit}
            disabled={isLoading || !reloadAddress || !reloadAddress.city || !reloadDate || selectedCargoIds.size === 0}
            startIcon={isLoading ? <CircularProgress size={16} /> : <CallSplitIcon />}
          >
            {isLoading ? 'Splitter...' : `Split ${selectedCargoIds.size} ordrer`}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
